/**
 * Atualização local da lista de conversas do inbox.
 *
 * Antes, cada mensagem nova disparava `recarregarConversas`, que refaz a lista
 * inteira no servidor. Com N atendentes de aba aberta, uma mensagem recebida
 * custava N execuções da query mais cara do backend. O payload de `nova_mensagem`
 * já traz tudo que a linha da lista precisa, então aplicamos localmente.
 *
 * A divergência acumulada é zerada sozinha: `todas_conversas` é reemitido em toda
 * reconexão do socket, troca de filtro de canal e F5.
 */

/** Mesma chave usada por `agruparConversas`. Precisa bater exatamente. */
export const chaveConversa = (telefone, instanciaId) =>
  `${telefone}_${instanciaId || 'sem_instancia'}`;

/**
 * Aplica uma mensagem nova no mapa de conversas.
 * Devolve `{ mapa, novaThread }` — `novaThread` indica que a conversa não existe
 * na lista e precisa de recarga para vir com avatar, tags e instância.
 */
export function aplicarNovaMensagem(mapa, msg) {
  const chave = chaveConversa(msg.telefone, msg.instancia_id);
  const atual = mapa[chave];

  if (!atual) return { mapa, novaThread: true };

  const ehEnviada = msg.direcao === 'enviada';
  const conteudo = msg.conteudo ?? atual.ultima_mensagem;

  const atualizada = {
    ...atual,
    ultima_mensagem: conteudo,
    eh_resposta: ehEnviada,
    create_at: msg.create_at || new Date().toISOString(),
    // Mensagem recebida deixa a conversa pendente; enviada resolve.
    tem_pendente: !ehEnviada,
    // Enviada: usa o status do payload. Recebida: a thread voltou a estar sem
    // resposta, e o recibo de entrega da mensagem anterior não vale mais — é o
    // mesmo invariante de agruparConversas (status só existe se respondida).
    ...(ehEnviada
      ? msg.status_mensagem
        ? { status_mensagem: msg.status_mensagem }
        : {}
      : { status_mensagem: null }),
    ...(ehEnviada && msg.respondida_por_humano !== undefined
      ? { respondida_por_humano: msg.respondida_por_humano }
      : {}),
    ...(msg.nome_contato ? { nome_contato: msg.nome_contato } : {}),
  };

  // A ordem da lista vem da ordem de inserção do objeto (o front não ordena),
  // então a conversa atualizada precisa ser reinserida primeiro para subir ao topo.
  const reordenado = { [chave]: atualizada };
  for (const k of Object.keys(mapa)) {
    if (k !== chave) reordenado[k] = mapa[k];
  }

  return { mapa: reordenado, novaThread: false };
}

/** Zera atendente, tags e pausa de IA em todas as threads de um telefone. */
export function aplicarAtendimentoFinalizado(mapa, telefone) {
  let mudou = false;
  const saida = { ...mapa };
  for (const chave of Object.keys(saida)) {
    if (saida[chave].telefone === telefone) {
      saida[chave] = {
        ...saida[chave],
        atendente_id: null,
        atendente_nome: null,
        tags: [],
        ia_pausada: false,
      };
      mudou = true;
    }
  }
  return mudou ? saida : mapa;
}
